const scenarios = ["app", "ring", "bench"] as const;
export const appFrames = ["dvh", "lvh", "band", "fixed"] as const;
export const accountSheets = ["owned", "edge-panel"] as const;
export const documentHeights = ["dvh", "lvh", "long"] as const;
export const bodyColors = ["page", "rose", "sky"] as const;
export const sheetPlacements = ["none", "in-page", "fixed"] as const;

export type AppFrame = (typeof appFrames)[number];
export type DocumentHeight = (typeof documentHeights)[number];
export type BodyColor = (typeof bodyColors)[number];

/**
 * Everything an experiment can set, all of it kept in the address so a reload
 * or a shared link lands on the same setup.
 *
 * `app` experiments are a small app — a header, a list and an account sheet —
 * in a frame of the chosen height. `ring` draws the edge of the display as the
 * page sees it. `bench` experiments size the document itself and draw a ruler
 * down it, so whatever shows under a bar can be read.
 */
export interface LabSettings {
  scenario: (typeof scenarios)[number];
  frame: AppFrame;
  accountSheet: (typeof accountSheets)[number];
  /**
   * Hand-set ring geometry for each orientation, as `top_bottom_left_right_radius`.
   * Left out, the ring uses what this phone is known to need.
   */
  ringPortrait?: string;
  ringLandscape?: string;
  height: DocumentHeight;
  cover: boolean;
  lockOverscroll: boolean;
  lockTouch: boolean;
  body: BodyColor;
  sheet: (typeof sheetPlacements)[number];
  scrim: boolean;
}

export const defaultLabSettings: LabSettings = {
  scenario: "app",
  frame: "band",
  accountSheet: "owned",
  height: "dvh",
  cover: false,
  lockOverscroll: false,
  lockTouch: false,
  body: "page",
  sheet: "none",
  scrim: false,
};

function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

function flag(value: unknown): boolean {
  return value === true || value === "true" || value === "1";
}

function ringOverride(value: unknown): string | undefined {
  return typeof value === "string" && /^-?\d+(_-?\d+){4}$/.test(value) ? value : undefined;
}

export function parseLabSettings(search: Record<string, unknown>): LabSettings {
  return {
    scenario: oneOf(scenarios, search.scenario, defaultLabSettings.scenario),
    frame: oneOf(appFrames, search.frame, defaultLabSettings.frame),
    accountSheet: oneOf(accountSheets, search.accountSheet, defaultLabSettings.accountSheet),
    ringPortrait: ringOverride(search.ringPortrait),
    ringLandscape: ringOverride(search.ringLandscape),
    height: oneOf(documentHeights, search.height, defaultLabSettings.height),
    cover: flag(search.cover),
    lockOverscroll: flag(search.lockOverscroll),
    lockTouch: flag(search.lockTouch),
    body: oneOf(bodyColors, search.body, defaultLabSettings.body),
    sheet: oneOf(sheetPlacements, search.sheet, defaultLabSettings.sheet),
    scrim: flag(search.scrim),
  };
}
